import { Button, HStack, Text } from "@chakra-ui/react";
import React, { memo, useCallback, useMemo } from "react";

import colors from "app/colors";
import { CopyIcon } from "modules/icons";
import {
  MessageFragment,
  MessageThreadFragment,
  MessageType,
} from "gql/graphql";
import Toast from "modules/toasts/toast";
import usePlainText from "modules/hooks/use-plain-text";
import useDisplayToast from "modules/hooks/use-display-toast";
import { quillDeltaToHtml } from "modules/quill/quill-delta-to-html";

interface IProps {
  messageThread: MessageThreadFragment;
  message: MessageFragment;
}

const MessagePopoverCopyMessageButton: React.FC<IProps> = ({
  messageThread,
  message,
}) => {
  const { plainText } = usePlainText(message.localizedText ?? message.text);
  const { displayToast } = useDisplayToast();

  const html = useMemo(
    () => quillDeltaToHtml(message.quillDelta),
    [message.quillDelta],
  );

  const messageToCopy = useMemo(() => {
    const voiceNotes = message.fileList.filter(
      (file) =>
        file.isVoiceNote && (file.voiceNoteText || file.voiceNoteLocalizedText),
    );

    const transcriptions = voiceNotes.map(
      (file) =>
        `Voice Transcription:\n"${file.voiceNoteLocalizedText ?? file.voiceNoteText}"`,
    );

    return [plainText ?? "", ...transcriptions].filter(Boolean).join("\n\n");
  }, [message.fileList, plainText]);

  const onCopyClicked = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      e.preventDefault();
      if (!messageToCopy) {
        displayToast(() => <Toast message="Nothing to Copy" />);
        return;
      }
      const blob = new ClipboardItem({
        "text/html": new Blob([html], { type: "text/html" }),
        "text/plain": new Blob([plainText ?? ""], { type: "text/plain" }),
      });

      await navigator.clipboard.write([blob]);

      displayToast(() => (
        <Toast
          message="Message copied!"
          leftIcon={<CopyIcon boxSize={5} fill={colors.grayMain} />}
        />
      ));
    },
    [displayToast, html, messageToCopy, plainText],
  );

  const isVisible = useMemo(() => {
    // Emails don't contain plain text, thus there is nothing to copy
    if (message.type === MessageType.Email) {
      return false;
    }

    if (messageThread.isLocked) {
      return false;
    }

    return true;
  }, [message.type, messageThread.isLocked]);

  if (!isVisible) {
    return null;
  }

  return (
    <Button variant="link" onClick={onCopyClicked}>
      <HStack spacing={2}>
        <CopyIcon boxSize={5} fill={colors.blue} />
        <Text fontSize={14} color={colors.blackSecondary}>
          Copy Message
        </Text>
      </HStack>
    </Button>
  );
};

export default memo(MessagePopoverCopyMessageButton);
